import React, { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, {
  configureAuth,
  getCurrentUser,
  getSubscriptionStatus,
  getAvailablePlans,
  activateTrial,
  checkAccess,
  invalidateSubscriptionCache,
  hasActiveAccess,
  calculateTrialDaysRemaining,
  formatSubscriptionStatus,
  getSubscriptionStatusColor,
} from "../api";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    isAuthenticated: false,
    user: null,
    loading: true,
    isAdmin: false,
    subscription: null,
    subscriptionLoading: false,
    subscriptionError: null,
    hasAccess: false,
    plans: [],
    plansLoading: false,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const fetchSubscriptionStatus = useCallback(
    async (silentFetch = false) => {
      if (!auth.isAuthenticated || !auth.user) {
        console.log(
          "[Subscription] User not authenticated, skipping subscription fetch"
        );
        return null;
      }

      try {
        if (!silentFetch) {
          setAuth((prev) => ({
            ...prev,
            subscriptionLoading: true,
            subscriptionError: null,
          }));
        }

        console.log("[Subscription] Fetching subscription status");
        const response = await getSubscriptionStatus();
        const subscriptionData = response.data;

        console.log(
          "[Subscription] Subscription data received:",
          subscriptionData
        );

        const subscription = subscriptionData.subscription;
        const hasAccess = subscriptionData.access?.has_access || false;
        const trialDaysRemaining = subscription
          ? calculateTrialDaysRemaining(subscription.trial_end)
          : 0;

        const enhancedSubscription = subscription
          ? {
              ...subscription,
              trial_days_remaining: trialDaysRemaining,
              status_display: formatSubscriptionStatus(subscription.status),
              status_color: getSubscriptionStatusColor(subscription.status),
              has_active_access: hasActiveAccess(subscription, auth.user),
            }
          : null;

        setAuth((prev) => ({
          ...prev,
          subscription: enhancedSubscription,
          hasAccess,
          subscriptionLoading: false,
          subscriptionError: null,
        }));

        return enhancedSubscription;
      } catch (error) {
        console.error(
          "[Subscription] Error fetching subscription status:",
          error
        );
        setAuth((prev) => ({
          ...prev,
          subscriptionLoading: false,
          subscriptionError:
            error.response?.data?.message ||
            "Failed to load subscription status",
        }));
        return null;
      }
    },
    [auth.isAuthenticated, auth.user]
  );

  const fetchAvailablePlans = useCallback(async () => {
    try {
      setAuth((prev) => ({ ...prev, plansLoading: true }));
      console.log("[Subscription] Fetching available plans");

      const response = await getAvailablePlans();
      const plans = response.data.plans || [];

      setAuth((prev) => ({
        ...prev,
        plans,
        plansLoading: false,
      }));

      return plans;
    } catch (error) {
      console.error("[Subscription] Error fetching plans:", error);
      setAuth((prev) => ({
        ...prev,
        plans: [],
        plansLoading: false,
      }));
      return [];
    }
  }, []);

  const activateTrialSubscription = useCallback(async () => {
    try {
      setAuth((prev) => ({
        ...prev,
        subscriptionLoading: true,
        subscriptionError: null,
      }));
      console.log("[Subscription] Activating trial subscription");

      const response = await activateTrial();

      if (response.data.success) {
        console.log("[Subscription] Trial activated successfully");
        await fetchSubscriptionStatus(true);
        return { success: true, message: response.data.message };
      } else {
        throw new Error(response.data.message || "Failed to activate trial");
      }
    } catch (error) {
      console.error("[Subscription] Error activating trial:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to activate trial";

      setAuth((prev) => ({
        ...prev,
        subscriptionLoading: false,
        subscriptionError: errorMessage,
      }));

      return { success: false, message: errorMessage };
    }
  }, [fetchSubscriptionStatus]);

  const checkUserAccess = useCallback(async (feature = "general") => {
    try {
      console.log(`[Subscription] Checking access for feature: ${feature}`);
      const response = await checkAccess(feature);

      const hasAccess = response.data.has_access || false;
      setAuth((prev) => ({ ...prev, hasAccess }));

      return hasAccess;
    } catch (error) {
      console.error("[Subscription] Error checking access:", error);
      return false;
    }
  }, []);

  const invalidateCache = useCallback(async () => {
    try {
      console.log("[Subscription] Invalidating subscription cache");
      await invalidateSubscriptionCache();
      await fetchSubscriptionStatus(true);
    } catch (error) {
      console.error("[Subscription] Error invalidating cache:", error);
    }
  }, [fetchSubscriptionStatus]);

  const logout = useCallback(() => {
    console.log("[Navigation Log] Logout function called");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("authToken");
    localStorage.removeItem("tokenType");
    configureAuth(null);
    api.defaults.headers.common["Authorization"] = "";
    setAuth({
      isAuthenticated: false,
      user: null,
      loading: false,
      isAdmin: false,
      subscription: null,
      subscriptionLoading: false,
      subscriptionError: null,
      hasAccess: false,
      plans: [],
      plansLoading: false,
    });
    console.log("[Navigation Log] Auth state reset, navigating to login");
    navigate("/login");
  }, [navigate]);

  const login = async (accessToken, refreshToken) => {
    try {
      console.log(
        "[Navigation Log] AuthContext login function called with tokens"
      );

      if (accessToken !== "cookie-auth") {
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);

        const isJwt =
          accessToken &&
          typeof accessToken === "string" &&
          accessToken.includes(".");
        localStorage.setItem("tokenType", isJwt ? "jwt" : "key");
        console.log(
          "[Navigation Log] Token type detected:",
          isJwt ? "jwt" : "key"
        );

        const authHeader = isJwt
          ? `Bearer ${accessToken}`
          : `Token ${accessToken}`;
        api.defaults.headers.common["Authorization"] = authHeader;
        console.log("[Navigation Log] Authorization header set");
      } else {
        console.log("[Navigation Log] Using cookie-based authentication");
        localStorage.setItem("tokenType", "cookie");

        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
      }

      console.log("[Navigation Log] Fetching user data");
      const userResponse = await api.get("/api/users/me/");
      console.log("User response data:", userResponse.data);

      const isAdmin =
        userResponse.data.is_staff || userResponse.data.is_superuser;
      console.log("Setting isAdmin to:", isAdmin);
      console.log("[Navigation Log] User is admin:", isAdmin);

      console.log("[Navigation Log] Updating auth state to authenticated");
      setAuth({
        isAuthenticated: true,
        user: userResponse.data,
        loading: false,
        isAdmin,
        subscription: null,
        subscriptionLoading: false,
        subscriptionError: null,
        hasAccess: isAdmin,
        plans: [],
        plansLoading: false,
      });

      console.log("[Navigation Log] Login function completed successfully");

      setTimeout(() => {
        fetchSubscriptionStatus(true);
        fetchAvailablePlans();
      }, 100);

      return true;
    } catch (error) {
      console.error("Login error:", error);
      console.log(
        "[Navigation Log] Login function failed with error:",
        error.message
      );
      logout();
      return false;
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      console.log(
        "[Navigation Log] Checking authentication state on path:",
        location.pathname
      );
      const accessToken = localStorage.getItem("accessToken");
      const refreshToken = localStorage.getItem("refreshToken");
      const tokenType = localStorage.getItem("tokenType") || "jwt";

      console.log("[Navigation Log] Tokens found:", {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken,
        tokenType,
      });

      if (!accessToken && tokenType !== "cookie") {
        console.log(
          "[Navigation Log] Missing access token and not using cookie auth, setting unauthenticated state"
        );
        setAuth({
          isAuthenticated: false,
          user: null,
          loading: false,
          isAdmin: false,
          subscription: null,
          subscriptionLoading: false,
          subscriptionError: null,
          hasAccess: false,
          plans: [],
          plansLoading: false,
        });

        if (
          location.pathname !== "/login" &&
          location.pathname !== "/register" &&
          location.pathname !== "/forgot-password" &&
          !location.pathname.startsWith("/reset-password/") &&
          !location.pathname.startsWith("/auth/google/callback")
        ) {
          console.log(
            "[Navigation Log] Not on auth page, storing redirect URL:",
            location.pathname
          );
          localStorage.setItem("redirectAfterLogin", location.pathname);
          console.log(
            "[Navigation Log] Redirecting to login from:",
            location.pathname
          );
          navigate("/login");
        }
        return;
      }

      try {
        if (tokenType !== "cookie" && accessToken) {
          console.log(
            "[Navigation Log] Setting auth header for token-based auth"
          );
          const authHeader =
            tokenType === "jwt"
              ? `Bearer ${accessToken}`
              : `Token ${accessToken}`;
          api.defaults.headers.common["Authorization"] = authHeader;
        } else {
          console.log("[Navigation Log] Using cookie-based authentication");
        }

        console.log("[Navigation Log] Fetching current user data");
        const userResponse = await getCurrentUser();
        console.log("User response data:", userResponse.data);

        const isAdmin =
          userResponse.data.is_staff || userResponse.data.is_superuser;
        console.log("Setting isAdmin to:", isAdmin);
        console.log(
          "[Navigation Log] Authentication successful, user is admin:",
          isAdmin
        );

        setAuth({
          isAuthenticated: true,
          user: userResponse.data,
          loading: false,
          isAdmin: isAdmin,
          subscription: null,
          subscriptionLoading: false,
          subscriptionError: null,
          hasAccess: isAdmin,
          plans: [],
          plansLoading: false,
        });
        console.log("[Navigation Log] Auth state updated to authenticated");

        setTimeout(() => {
          fetchSubscriptionStatus(true);
          fetchAvailablePlans();
        }, 100);
      } catch (err) {
        console.error("Auth check error:", err);
        console.log(
          "[Navigation Log] Authentication check failed:",
          err.message
        );
        logout();
      }
    };

    checkAuth();
  }, [navigate, location.pathname, logout]);

  const contextValue = {
    auth,
    setAuth,
    logout,
    login,
    subscription: {
      data: auth.subscription,
      loading: auth.subscriptionLoading,
      error: auth.subscriptionError,
      hasAccess: auth.hasAccess,
      plans: auth.plans,
      plansLoading: auth.plansLoading,
      refresh: fetchSubscriptionStatus,
      activateTrial: activateTrialSubscription,
      checkAccess: checkUserAccess,
      invalidateCache,
      fetchPlans: fetchAvailablePlans,
    },
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;

export const refreshToken = (refresh) => {
  const tokenType = localStorage.getItem("tokenType") || "jwt";

  if (tokenType === "jwt") {
    return api.post(
      "/api/token/refresh/",
      { refresh },
      {
        timeout: 30000,
      }
    );
  } else {
    return Promise.resolve({
      data: {
        access: refresh,
        refresh: refresh,
      },
    });
  }
};
